import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Alert, Table
} from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { matchesSearch } from "../../../helpers/search";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const DURUM_CONFIG = {
  Beklemede:  { color: "warning",  icon: "bx-time-five",      label: "Beklemede" },
  Onaylandı:  { color: "success",  icon: "bx-check-circle",   label: "Onaylandı" },
  Onaylandi:  { color: "success",  icon: "bx-check-circle",   label: "Onaylandı" },
  İptal:      { color: "danger",   icon: "bx-x-circle",       label: "İptal" },
  Iptal:      { color: "danger",   icon: "bx-x-circle",       label: "İptal" },
};

const durumBadge = (durum) => {
  const cfg = DURUM_CONFIG[durum] || { color: "secondary", icon: "bx-question-mark", label: durum || "-" };
  return (
    <Badge color={cfg.color} className="d-inline-flex align-items-center gap-1 px-2 py-1">
      <i className={`bx ${cfg.icon}`} /> {cfg.label}
    </Badge>
  );
};

const FILTERS = [
  { key: "all",       label: "Tümü" },
  { key: "Beklemede", label: "Beklemede" },
  { key: "Onaylandı", label: "Onaylandı" },
  { key: "İptal",     label: "İptal" },
];

const KurbanSiparisYonetimi = () => {
  document.title = "Kurban Sipariş Yönetimi | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = useMemo(() => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }), [token]);

  const [siparisler, setSiparisler] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [isUpdating, setIsUpdating] = useState(null); // siparis ID

  // Detay modal
  const [detayModal, setDetayModal] = useState(false);
  const [seciliSiparis, setSeciliSiparis] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${BASE_URL}/KURBAN_HISSE_SIPARISLERI?$orderby=SIPARIS_TARIHI desc&$top=500`,
        { headers }
      );
      const data = await res.json();
      setSiparisler(data.value || []);
    } catch {
      setError("Siparişler yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDurumGuncelle = async (id, yeniDurum) => {
    setIsUpdating(id);
    try {
      const res = await fetch(`${BASE_URL}/KURBAN_HISSE_SIPARISLERI(${id})`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ ODEME_DURUMU: yeniDurum }),
      });
      if (res.ok) {
        setSuccessMsg(`Sipariş #${id} → ${yeniDurum} olarak güncellendi.`);
        setSiparisler(prev =>
          prev.map(s => s.ID === id ? { ...s, ODEME_DURUMU: yeniDurum } : s)
        );
        if (seciliSiparis?.ID === id) {
          setSeciliSiparis(prev => ({ ...prev, ODEME_DURUMU: yeniDurum }));
        }
        setTimeout(() => setSuccessMsg(""), 3000);
      } else {
        setError("Güncelleme başarısız: " + res.status);
      }
    } catch {
      setError("Güncelleme sırasında hata oluştu.");
    } finally {
      setIsUpdating(null);
    }
  };

  const filteredSiparisler = useMemo(() => {
    let list = siparisler;

    if (activeFilter !== "all") {
      list = list.filter(s => {
        const d = s.ODEME_DURUMU || "";
        if (activeFilter === "Onaylandı") return d === "Onaylandı" || d === "Onaylandi";
        if (activeFilter === "İptal") return d === "İptal" || d === "Iptal";
        return d === activeFilter;
      });
    }

    if (search.trim()) {
      // Kaydın tüm alanları (genişletilmiş alıcı/satıcı/ilan adları dahil).
      list = list.filter(s => matchesSearch(s, search));
    }

    return list;
  }, [siparisler, activeFilter, search]);

  const stats = useMemo(() => {
    const bekleyen = siparisler.filter(s => s.ODEME_DURUMU === "Beklemede").length;
    const onaylanan = siparisler.filter(s => s.ODEME_DURUMU === "Onaylandı" || s.ODEME_DURUMU === "Onaylandi").length;
    const iptal = siparisler.filter(s => s.ODEME_DURUMU === "İptal" || s.ODEME_DURUMU === "Iptal").length;
    const toplamTutar = siparisler
      .filter(s => s.ODEME_DURUMU === "Onaylandı" || s.ODEME_DURUMU === "Onaylandi")
      .reduce((acc, s) => acc + (parseFloat(s.TOPLAM_FIYAT) || 0), 0);
    return { toplam: siparisler.length, bekleyen, onaylanan, iptal, toplamTutar };
  }, [siparisler]);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Yönetim" breadcrumbItem="Kurban Sipariş Yönetimi" />

          {successMsg && (
            <Alert color="success" className="border-0" toggle={() => setSuccessMsg("")}>
              <i className="mdi mdi-check-circle me-2" />{successMsg}
            </Alert>
          )}
          {error && (
            <Alert color="danger" className="border-0" toggle={() => setError(null)}>
              <i className="mdi mdi-alert-outline me-2" />{error}
            </Alert>
          )}

          {/* İstatistik kartları */}
          <Row className="mb-4">
            {[
              { label: "Toplam Sipariş",   value: stats.toplam,    icon: "bx-list-ul",       color: "primary" },
              { label: "Bekleyen",          value: stats.bekleyen,  icon: "bx-time-five",     color: "warning" },
              { label: "Onaylanan",         value: stats.onaylanan, icon: "bx-check-circle",  color: "success" },
              { label: "İptal",             value: stats.iptal,     icon: "bx-x-circle",      color: "danger" },
              {
                label: "Onaylanan Toplam",
                value: stats.toplamTutar.toLocaleString("tr-TR", { minimumFractionDigits: 0 }) + " ₺",
                icon: "bx-lira",
                color: "info",
              },
            ].map((s, i) => (
              <Col key={i} xs={6} md={4} xl>
                <Card className="mini-stats-wid mb-3">
                  <CardBody>
                    <div className="d-flex">
                      <div className="flex-grow-1">
                        <p className="text-muted fw-medium mb-1 font-size-13">{s.label}</p>
                        <h4 className="mb-0">
                          {isLoading ? <Spinner size="sm" color={s.color} /> : s.value}
                        </h4>
                      </div>
                      <div className={`avatar-sm rounded-circle bg-${s.color} align-self-center mini-stat-icon`}>
                        <span className={`avatar-title rounded-circle bg-${s.color}`}>
                          <i className={`bx ${s.icon} font-size-20`} />
                        </span>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Ana tablo */}
          <Card className="shadow-sm">
            <CardBody>
              {/* Toolbar */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
                <div>
                  <h4 className="card-title mb-1">Kurban Hisse Siparişleri</h4>
                  <p className="text-muted mb-0 font-size-13">
                    Tüm kurban ve adak hisse sipariş taleplerini yönetin.
                  </p>
                </div>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <Input
                    type="search"
                    placeholder="Sipariş / ilan / kullanıcı ara..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ width: 230 }}
                    bsSize="sm"
                  />
                  <div className="btn-group btn-group-sm">
                    {FILTERS.map(f => (
                      <button
                        key={f.key}
                        className={`btn ${activeFilter === f.key ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setActiveFilter(f.key)}
                      >
                        {f.label}
                        {f.key === "Beklemede" && stats.bekleyen > 0 && (
                          <span className="badge bg-danger ms-1">{stats.bekleyen}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <Button color="secondary" size="sm" outline onClick={fetchAll} disabled={isLoading}>
                    <i className="bx bx-refresh me-1" />Yenile
                  </Button>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-3 text-muted">Yükleniyor...</p>
                </div>
              ) : filteredSiparisler.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bx bx-package display-4 mb-3 opacity-50" />
                  <h5>Sipariş bulunamadı.</h5>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="table align-middle table-hover mb-0" bordered>
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        <th>İlan ID</th>
                        <th>Alıcı ID</th>
                        <th>Satıcı ID</th>
                        <th>Hisse Tipi</th>
                        <th>Adet</th>
                        <th className="text-end">Toplam</th>
                        <th>Ödeme Tipi</th>
                        <th>Durum</th>
                        <th>Tarih</th>
                        <th style={{ width: 180 }}>İşlemler</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSiparisler.map(s => (
                        <tr key={s.ID}>
                          <td><span className="fw-medium text-muted">#{s.ID}</span></td>
                          <td>
                            <span className="badge bg-light text-dark border">İlan #{s.ILAN_ID}</span>
                          </td>
                          <td>
                            <span className="text-primary fw-medium">#{s.ALICI_ID}</span>
                          </td>
                          <td>
                            <span className="text-dark fw-medium">#{s.SATICI_ID}</span>
                          </td>
                          <td>
                            {s.HISSE_TIP
                              ? <Badge color="info" className="font-size-11">{s.HISSE_TIP}</Badge>
                              : <span className="text-muted font-size-11">Tümü</span>
                            }
                          </td>
                          <td className="text-center fw-medium">{s.HISSE_ADET ?? 1}</td>
                          <td className="text-end fw-bold text-primary">
                            {s.TOPLAM_FIYAT
                              ? Number(s.TOPLAM_FIYAT).toLocaleString("tr-TR") + " ₺"
                              : "-"}
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border font-size-11">
                              {s.SATIN_ALMA_TIPI || "-"} / {s.ODEME_YONTEMI || "-"}
                            </span>
                          </td>
                          <td>{durumBadge(s.ODEME_DURUMU)}</td>
                          <td className="text-muted font-size-12">
                            {s.SIPARIS_TARIHI
                              ? new Date(s.SIPARIS_TARIHI).toLocaleDateString("tr-TR", {
                                  day: "2-digit", month: "2-digit", year: "numeric",
                                })
                              : "-"}
                          </td>
                          <td>
                            <div className="d-flex gap-1">
                              <Button
                                color="light"
                                size="sm"
                                className="border"
                                title="Detay"
                                onClick={() => { setSeciliSiparis(s); setDetayModal(true); }}
                              >
                                <i className="bx bx-show" />
                              </Button>
                              {(s.ODEME_DURUMU === "Beklemede") && (
                                <>
                                  <Button
                                    color="success"
                                    size="sm"
                                    title="Onayla"
                                    disabled={isUpdating === s.ID}
                                    onClick={() => handleDurumGuncelle(s.ID, "Onaylandı")}
                                  >
                                    {isUpdating === s.ID
                                      ? <Spinner size="sm" />
                                      : <i className="bx bx-check" />
                                    }
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    title="İptal Et"
                                    disabled={isUpdating === s.ID}
                                    onClick={() => handleDurumGuncelle(s.ID, "İptal")}
                                  >
                                    {isUpdating === s.ID
                                      ? <Spinner size="sm" />
                                      : <i className="bx bx-x" />
                                    }
                                  </Button>
                                </>
                              )}
                              {(s.ODEME_DURUMU === "İptal" || s.ODEME_DURUMU === "Iptal") && (
                                <Button
                                  color="warning"
                                  size="sm"
                                  title="Tekrar Beklemede Yap"
                                  disabled={isUpdating === s.ID}
                                  onClick={() => handleDurumGuncelle(s.ID, "Beklemede")}
                                >
                                  <i className="bx bx-reset" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      {/* Detay Modal */}
      <Modal isOpen={detayModal} toggle={() => setDetayModal(false)} centered size="md">
        <ModalHeader toggle={() => setDetayModal(false)} className="bg-light">
          <i className="bx bx-receipt me-2 text-primary" />
          Sipariş Detayı — #{seciliSiparis?.ID}
        </ModalHeader>
        <ModalBody>
          {seciliSiparis && (
            <div>
              <Row className="g-3">
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">İlan</div>
                    <div className="fw-bold">#{seciliSiparis.ILAN_ID}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Satın Alma Tipi</div>
                    <div className="fw-bold">{seciliSiparis.SATIN_ALMA_TIPI || "-"}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Alıcı ID</div>
                    <div className="fw-bold text-primary">#{seciliSiparis.ALICI_ID}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Satıcı ID</div>
                    <div className="fw-bold">#{seciliSiparis.SATICI_ID}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Hisse Tipi</div>
                    <div className="fw-bold">{seciliSiparis.HISSE_TIP || "Tüm Kurban"}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Adet</div>
                    <div className="fw-bold">{seciliSiparis.HISSE_ADET ?? 1}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Hisse Birim Fiyat</div>
                    <div className="fw-bold text-primary">
                      {seciliSiparis.HISSE_FIYAT
                        ? Number(seciliSiparis.HISSE_FIYAT).toLocaleString("tr-TR") + " ₺"
                        : "-"}
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Toplam Tutar</div>
                    <div className="fw-bold text-success font-size-16">
                      {seciliSiparis.TOPLAM_FIYAT
                        ? Number(seciliSiparis.TOPLAM_FIYAT).toLocaleString("tr-TR") + " ₺"
                        : "-"}
                    </div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Ödeme Yöntemi</div>
                    <div className="fw-bold">{seciliSiparis.ODEME_YONTEMI || "-"}</div>
                  </div>
                </Col>
                <Col xs={6}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Kart Son 4</div>
                    <div className="fw-bold">
                      {seciliSiparis.KART_SON_DORT ? `**** ${seciliSiparis.KART_SON_DORT}` : "-"}
                    </div>
                  </div>
                </Col>
                <Col xs={12}>
                  <div className="p-3 bg-light rounded border">
                    <div className="text-muted font-size-11 mb-1">Sipariş Tarihi</div>
                    <div className="fw-bold">
                      {seciliSiparis.SIPARIS_TARIHI
                        ? new Date(seciliSiparis.SIPARIS_TARIHI).toLocaleString("tr-TR")
                        : "-"}
                    </div>
                  </div>
                </Col>
                {seciliSiparis.NOT_ALICI && (
                  <Col xs={12}>
                    <div className="p-3 bg-warning bg-opacity-10 border border-warning rounded">
                      <div className="text-muted font-size-11 mb-1">Alıcı Notu</div>
                      <div>{seciliSiparis.NOT_ALICI}</div>
                    </div>
                  </Col>
                )}
                <Col xs={12}>
                  <div className="p-3 rounded border text-center">
                    <div className="text-muted font-size-11 mb-2">Ödeme Durumu</div>
                    {durumBadge(seciliSiparis.ODEME_DURUMU)}
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="gap-2">
          {seciliSiparis?.ODEME_DURUMU === "Beklemede" && (
            <>
              <Button
                color="success"
                disabled={isUpdating === seciliSiparis?.ID}
                onClick={() => handleDurumGuncelle(seciliSiparis.ID, "Onaylandı")}
              >
                {isUpdating === seciliSiparis?.ID ? <Spinner size="sm" /> : <i className="bx bx-check me-1" />}
                Onayla
              </Button>
              <Button
                color="danger"
                disabled={isUpdating === seciliSiparis?.ID}
                onClick={() => handleDurumGuncelle(seciliSiparis.ID, "İptal")}
              >
                {isUpdating === seciliSiparis?.ID ? <Spinner size="sm" /> : <i className="bx bx-x me-1" />}
                İptal Et
              </Button>
            </>
          )}
          <Button color="secondary" outline onClick={() => setDetayModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default KurbanSiparisYonetimi;
